import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';

interface Profile {
  id: string;
  name: string;
}

interface NewChatDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const NewChatDialog = ({ open, onClose, onCreated }: NewChatDialogProps) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    const fetchProfiles = async () => {
      // Admins can see all, employees see own. Let's fetch what RLS allows.
      const { data } = await supabase.from('profiles_public' as any).select('id, name') as any;
      setProfiles(((data as any[]) || []).filter((p: any) => p.id !== user.id));
    };
    fetchProfiles();
  }, [open, user]);

  const filtered = profiles.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

  const startChat = async (otherUserId: string) => {
    if (!user || creating) return;
    setCreating(true);

    // Check if direct room already exists between these two users
    const { data: myRooms } = await supabase
      .from('chat_room_members')
      .select('room_id')
      .eq('user_id', user.id);

    const { data: theirRooms } = await supabase
      .from('chat_room_members')
      .select('room_id')
      .eq('user_id', otherUserId);

    const myRoomIds = new Set(myRooms?.map(r => r.room_id) || []);
    const sharedRoomIds = (theirRooms || []).filter(r => myRoomIds.has(r.room_id)).map(r => r.room_id);

    if (sharedRoomIds.length > 0) {
      // Check if any shared room is a direct chat
      const { data: directRooms } = await supabase
        .from('chat_rooms')
        .select('id')
        .in('id', sharedRoomIds)
        .eq('type', 'direct');

      if (directRooms && directRooms.length > 0) {
        // Room already exists
        setCreating(false);
        onClose();
        onCreated();
        return;
      }
    }

    // Create new direct room
    const { data: newRoom } = await supabase
      .from('chat_rooms')
      .insert({ type: 'direct', created_by: user.id })
      .select()
      .single();

    if (newRoom) {
      await supabase.from('chat_room_members').insert([
        { room_id: newRoom.id, user_id: user.id },
        { room_id: newRoom.id, user_id: otherUserId },
      ]);
    }

    setCreating(false);
    onClose();
    onCreated();
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#25D366', '#128C7E', '#075E54', '#34B7F1', '#00A884'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        <DialogHeader className="bg-[#075E54] text-white px-4 py-3">
          <DialogTitle className="text-white">{t('newChat')}</DialogTitle>
        </DialogHeader>
        <div className="px-3 py-2 bg-[#f0f0f0]">
          <div className="relative">
            <Search className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#667781]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('searchContacts')}
              className="pl-10 rtl:pl-3 rtl:pr-10 rounded-full bg-white border-0 text-sm"
            />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {filtered.map((profile) => (
            <div
              key={profile.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-[#f0f0f0] cursor-pointer border-b border-[#e9e9e9]"
              onClick={() => startChat(profile.id)}
            >
              <Avatar className="h-11 w-11">
                <AvatarFallback style={{ backgroundColor: getAvatarColor(profile.name) }} className="text-white font-medium">
                  {profile.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-[#111b21] font-medium">{profile.name}</span>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-[#667781] py-6 text-sm">{t('noUsers')}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewChatDialog;
